import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Grid,
  Card,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  Avatar,
  Divider,
  IconButton,
  Fade,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SpeedIcon from '@mui/icons-material/Speed';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import CancelIcon from '@mui/icons-material/Cancel';
import VerifiedIcon from '@mui/icons-material/Verified';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BuildIcon from '@mui/icons-material/Build';
import CommentIcon from '@mui/icons-material/Comment';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import GavelIcon from '@mui/icons-material/Gavel';
import BadgeIcon from '@mui/icons-material/Badge';
import { useNavigate, useParams } from 'react-router-dom';
import IDScannerModal from '../../components/FieldAgent/IDScannerModal';
import {
  agencyService,
  reservationService,
  subClientService,
  pricingService,
  prestationService,
  vehicleTypeService,
  prestationResolveService,
} from '../../services/api';
import type { Agency, SubClient, PricingPlan, PricingPack, Prestation, VehicleType, ReservationAvailabilityParams } from '../../types';

const STEPS_CREATE = [
  { id: 'dates_vehicle', label: 'Dates & Véhicule', icon: <DirectionsCarIcon /> },
  { id: 'client', label: 'Client', icon: <PersonIcon /> },
  { id: 'tarif', label: 'Tarification', icon: <ReceiptIcon /> },
  { id: 'reglement', label: 'Règlement', icon: <PaymentIcon /> },
  { id: 'comments', label: 'Commentaires', icon: <CommentIcon /> },
];

const STEPS_EDIT = [
  { id: 'dates_vehicle', label: 'Dates & Véhicule', icon: <DirectionsCarIcon /> },
  { id: 'client', label: 'Client', icon: <PersonIcon /> },
  { id: 'tarif', label: 'Tarification', icon: <ReceiptIcon /> },
  { id: 'reglement', label: 'Règlement', icon: <PaymentIcon /> },
  { id: 'comments', label: 'Commentaires', icon: <CommentIcon /> },
  { id: 'documents', label: 'Documents et pièces jointes', icon: <FolderOutlinedIcon /> },
  { id: 'mailings', label: 'Mailing', icon: <EmailOutlinedIcon /> },
];

const HERO_IMAGE = 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=1920&q=80';

const RESERVATION_MAILING_TYPE_LABELS: Record<string, string> = {
  bon_de_commande: 'Bon de commande',
  contrat_location: 'Contrat de location',
  recapitulatif: 'Récapitulatif',
  relance_personnalisee: 'Message personnalisé',
};

const PAYMENT_MODES = ['CB', 'American Express', 'Virement', 'Espèces', 'Chèque', 'En agence'];

interface AvailabilityVehicleItem {
  id: number;
  registration_number?: string;
  marque?: string;
  modele?: string;
  year?: number;
  mileage?: number;
  status?: string;
  fuel_type?: string;
  transmission_type?: string;
  nb_places?: number;
  nb_portes?: number;
  primary_image?: string | null;
  primary_image_url?: string | null;
  vehicle_category_id?: number;
  vehicle_type_id?: number;
  maintenance_status?: string;
  date_derniere_intervention?: string | null;
  is_stop_sell_blocked?: boolean | number;
}

interface AvailabilityCategory {
  vehicle_category_id: number;
  vehicle_type_id?: number;
  category_name: string;
  vehicle_type_name: string;
  image_url?: string | null;
  rate_ht?: number | null;
  rate_ttc?: number | null;
  available_count: number;
  vehicles: AvailabilityVehicleItem[];
}

const normalizePhoneDigits = (s: string): string => s.replace(/\D/g, '');

const subClientDisplayName = (c: SubClient): string => {
  if (c.type === 'company' && c.company_name) return c.company_name;
  if (c.name) return c.name;
  return [c.prenom, c.nom].filter(Boolean).join(' ') || '—';
};

function calculateRentalDuration(pickupDt: string, returnDt: string): number {
  if (!pickupDt || !returnDt) return 0;
  const depart = new Date(pickupDt);
  const retour = new Date(returnDt);
  if (isNaN(depart.getTime()) || isNaN(retour.getTime()) || retour <= depart) return 0;
  if (depart.toDateString() === retour.toDateString()) return 1;

  let nbJours = 0;
  const current = new Date(depart);
  current.setHours(0, 0, 0, 0);
  const retourDate = new Date(retour);
  retourDate.setHours(0, 0, 0, 0);

  while (current <= retourDate) {
    if (current.toDateString() === retourDate.toDateString()) {
      const departMinutes = depart.getHours() * 60 + depart.getMinutes();
      const retourMinutes = retour.getHours() * 60 + retour.getMinutes();
      if (retourMinutes - departMinutes > 120) nbJours++;
    } else {
      nbJours++;
    }
    current.setDate(current.getDate() + 1);
  }
  return Math.max(1, nbJours);
}

function eachCalendarDayYmd(pickupIso: string, returnIso: string): string[] {
  if (!pickupIso || !returnIso) return [];
  const a = pickupIso.slice(0, 10);
  const b = returnIso.slice(0, 10);
  if (a > b) return [];
  const out: string[] = [];
  let cur = a;
  while (true) {
    out.push(cur);
    if (cur >= b) break;
    const [y, m, d] = cur.split('-').map((x) => parseInt(x, 10));
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + 1);
    const yy = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(dt.getUTCDate()).padStart(2, '0');
    cur = `${yy}-${mm}-${dd}`;
  }
  return out;
}

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (status) {
    case 'confirmed': return 'success';
    case 'pending': return 'warning';
    case 'cancelled': return 'error';
    case 'completed': return 'info';
    default: return 'default';
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'confirmed': return 'Confirmée';
    case 'pending': return 'En attente';
    case 'cancelled': return 'Annulée';
    case 'completed': return 'Terminée';
    default: return status;
  }
};

const ReservationCreate: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const currentSteps = isEdit ? STEPS_EDIT : STEPS_CREATE;

  const [step, setStep] = useState(0);
  const currentStepId = currentSteps[step]?.id;

  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loadingAgencies, setLoadingAgencies] = useState(true);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [categories, setCategories] = useState<AvailabilityCategory[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [categoryForModal, setCategoryForModal] = useState<AvailabilityCategory | null>(null);
  const [vehicleSearchText, setVehicleSearchText] = useState('');
  const [stopSellForceOpen, setStopSellForceOpen] = useState(false);
  const [pendingStopSellVehicle, setPendingStopSellVehicle] = useState<AvailabilityVehicleItem | null>(null);
  const [idScannerOpen, setIdScannerOpen] = useState(false);
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [clientSearchDialogOpen, setClientSearchDialogOpen] = useState(false);
  const [clientConfirmDialogOpen, setClientConfirmDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedExistingClient, setSelectedExistingClient] = useState<SubClient | null>(null);
  const [clientModified, setClientModified] = useState(false);
  const [duplicateEmailClient, setDuplicateEmailClient] = useState<SubClient | null>(null);
  const [duplicatePhoneClient, setDuplicatePhoneClient] = useState<SubClient | null>(null);
  const [loadingBaseRate, setLoadingBaseRate] = useState(false);

  // Prevent stale async responses from re-populating prestations after switching offer type
  const prestationsLoadTokenRef = useRef(0);

  const [comments, setComments] = useState<any[]>([]);
  const [newCommentText, setNewCommentText] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [mailings, setMailings] = useState<any[]>([]);
  const [loadingMailings, setLoadingMailings] = useState(false);
  const [relanceDialogOpen, setRelanceDialogOpen] = useState(false);
  const [relanceSubject, setRelanceSubject] = useState('');
  const [relanceBody, setRelanceBody] = useState('');
  const [relanceSentTo, setRelanceSentTo] = useState('');
  const [sendingRelance, setSendingRelance] = useState(false);
  const [stockWarnings, setStockWarnings] = useState<any[]>([]);
  const [stockWarningsAcknowledged, setStockWarningsAcknowledged] = useState(false);
  const [sendingBonLoading, setSendingBonLoading] = useState(false);
  const [sendingContractLoading, setSendingContractLoading] = useState(false);
  const [pendingCreateComment, setPendingCreateComment] = useState('');
  const [availabilityLoaded, setAvailabilityLoaded] = useState(false);
  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); }
    catch { return {}; }
  }, []);

  const [form, setForm] = useState({
    pickup_datetime: '',
    return_datetime: '',
    pickup_agency_id: '' as number | '',
    return_agency_id: '' as number | '',
    vehicle_type_id: '' as number | '',
    transmission: '',
    sub_client_id: '' as number | '',
    customer_civility: '',
    customer_type: 'individual' as 'individual' | 'company',
    customer_first_name: '',
    customer_last_name: '',
    customer_email: '',
    customer_phone: '',
    customer_mobile: '',
    customer_company: '',
    customer_siret: '',
    customer_address: '',
    customer_address_complement: '',
    customer_postal_code: '',
    customer_city: '',
    customer_country: '',
    customer_birth_date: '',
    customer_birth_place: '',
    customer_accept_newsletter: false,
    customer_is_active: true,
    pricing_plan_id: '' as number | '',
    pricing_pack_id: '' as number | '',
    pack_name: '',
    duration: 0,
    discount_pct: '',
    discount_days: '',
    daily_rate: 0,
    franchise_amount: '',
    franchise_supplement_per_day: '',
    standard_rate_per_day: '',
    prestations: [] as any[],
    vehicle_id: '' as number | '',
    payments: [] as any[],
    reservation_reference: '',
  });

  const [clientSearch, setClientSearch] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState<SubClient[]>([]);
  const [loadingClientSearch, setLoadingClientSearch] = useState(false);
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [packs, setPacks] = useState<PricingPack[]>([]);
  const [prestations, setPrestations] = useState<Prestation[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [selectedVehicleTypeIds, setSelectedVehicleTypeIds] = useState<number[]>([]);
  const [selectedTransmissions, setSelectedTransmissions] = useState<string[]>([]);

  useEffect(() => {
    agencyService.getAll().then((res) => setAgencies(res.data.data || [])).catch(() => setAgencies([])).finally(() => setLoadingAgencies(false));
  }, []);

  useEffect(() => {
    vehicleTypeService.getAll().then((res) => setVehicleTypes(res.data.data || [])).catch(() => setVehicleTypes([]));
    pricingService.getPlans().then((res) => setPlans(res.data.data || [])).catch(() => setPlans([]));
    prestationService.getPrestations().then((res) => setPrestations(res.data.data || [])).catch(() => setPrestations([]));
  }, []);

  useEffect(() => {
    if (!id || !isEdit) return;
    reservationService.getById(Number(id)).then((res) => {
      const r = res.data.data as any;
      if (!r) return;
      setForm((f) => ({
        ...f,
        pickup_datetime: r.pickup_datetime ? r.pickup_datetime.slice(0, 16) : '',
        return_datetime: r.return_datetime ? r.return_datetime.slice(0, 16) : '',
        pickup_agency_id: r.pickup_agency_id ?? '',
        return_agency_id: r.return_agency_id ?? '',
        vehicle_id: r.vehicle_id ?? '',
        vehicle_type_id: r.vehicle?.vehicle_type_id ?? '',
        sub_client_id: r.sub_client_id ?? '',
        customer_civility: r.customer_civility ?? '',
        customer_first_name: r.customer_first_name ?? '',
        customer_last_name: r.customer_last_name ?? '',
        customer_email: r.customer_email ?? '',
        customer_phone: r.customer_phone ?? '',
        customer_company: r.customer_company ?? '',
        customer_address: r.customer_address ?? '',
        customer_postal_code: r.customer_postal_code ?? '',
        customer_city: r.customer_city ?? '',
        customer_country: r.customer_country ?? '',
        customer_birth_date: r.customer_birth_date ?? '',
        customer_birth_place: r.customer_birth_place ?? '',
        pricing_plan_id: r.pricing_plan_id ?? '',
        pricing_pack_id: r.pricing_pack_id ?? '',
        pack_name: r.pack_name ?? '',
        duration: r.rental_days ?? 0,
        daily_rate: Number(r.daily_rate ?? 0) || 0,
        franchise_amount: r.franchise_amount != null ? String(r.franchise_amount) : '',
        prestations: ((r.reservation_prestations || r.reservationPrestations || []) as any[]).map((p: any) => ({
          prestation_id: p.prestation_id,
          name: p.name,
          is_included: !!p.is_included,
          pricing_mode: p.pricing_mode || 'flat',
          unit_rate: p.unit_rate ?? 0,
          tax_rate: p.tax_rate ?? 20,
          quantity: p.quantity ?? 1,
          total_amount: p.total_amount ?? 0,
        })),
        reservation_reference: typeof r.reference === 'string' ? r.reference : r.reference != null ? String(r.reference) : '',
      }));
      if (r.sub_client) setSelectedExistingClient(r.sub_client);
      if (r.vehicle) {
        setSelectedVehicle({
          id: r.vehicle.id, registration_number: r.vehicle.registration_number, marque: r.vehicle.marque, modele: r.vehicle.modele,
          year: r.vehicle.year, mileage: r.vehicle.mileage, status: r.vehicle.status, vehicle_category_id: r.vehicle.vehicle_category_id,
          primary_image_url: r.vehicle.primary_image_url, transmission_type: r.vehicle.transmission_type, fuel_type: r.vehicle.fuel_type,
          nb_places: r.vehicle.nb_places, nb_portes: r.vehicle.nb_portes, maintenance_status: r.vehicle.maintenance_status,
          date_derniere_intervention: r.vehicle.date_derniere_intervention,
        });
      }
    }).catch(() => setError('Impossible de charger la réservation.'));

    reservationService.getPayments(Number(id)).then((res) => {
      const payments = (res.data.data || []).map((p: any) => ({
        id: p.id,
        type: p.type || 'reglement',
        mode: p.mode || '',
        label: p.label || '',
        amount: p.amount != null ? String(p.amount) : '',
        payment_date: p.payment_date || '',
        due_date: p.due_date || '',
        agency_id: p.agency_id || '',
        agent_name: p.agent_name || '',
      }));
      setForm((f) => ({ ...f, payments }));
    }).catch(() => {});
  }, [id, isEdit]);

  useEffect(() => {
    if (!id || !isEdit) return;
    setLoadingComments(true);
    reservationService.getComments(Number(id))
      .then((res) => setComments(res.data.data || []))
      .catch(() => setComments([]))
      .finally(() => setLoadingComments(false));
  }, [id, isEdit]);

  useEffect(() => {
    if (!id || !isEdit) return;
    setLoadingMailings(true);
    reservationService.getMailings(Number(id))
      .then((res) => setMailings(res.data.data || []))
      .catch(() => setMailings([]))
      .finally(() => setLoadingMailings(false));
  }, [id, isEdit]);

  const computedDuration = useMemo(() => calculateRentalDuration(form.pickup_datetime, form.return_datetime), [form.pickup_datetime, form.return_datetime]);

  useEffect(() => {
    if (computedDuration > 0) setForm((f) => f.duration === computedDuration ? f : { ...f, duration: computedDuration });
  }, [computedDuration]);

  // Reset stock warnings whenever the user changes dates or prestations
  useEffect(() => {
    setStockWarnings([]);
    setStockWarningsAcknowledged(false);
  }, [form.pickup_datetime, form.return_datetime, form.prestations]);

  const isOffreLibre = form.pack_name === 'Offre libre';

  // In pack mode, prestations marked as included must NOT be charged again.
  const prestationsTotalHT = useMemo(
    () => form.prestations.reduce((sum, row) => {
      if (row.is_included) return sum;
      return sum + (row.quantity ?? 1) * (row.unit_rate ?? 0);
    }, 0),
    [form.prestations]
  );
  const prestationsTotalTTC = useMemo(
    () => form.prestations.reduce((sum, row) => {
      if (row.is_included) return sum;
      const tva = row.tax_rate ?? 20;
      return sum + (row.quantity ?? 1) * (row.unit_rate ?? 0) * (1 + tva / 100);
    }, 0),
    [form.prestations]
  );

  const effectiveDays = useMemo(() => {
    const days = form.duration || computedDuration;
    const discountDays = parseInt(form.discount_days || '0', 10) || 0;
    return Math.max(1, days - discountDays);
  }, [form.duration, computedDuration, form.discount_days]);

  const baseRentalHT = useMemo(() => Number(form.daily_rate || 0) * effectiveDays, [form.daily_rate, effectiveDays]);
  const subtotalHT = useMemo(() => baseRentalHT + prestationsTotalHT, [baseRentalHT, prestationsTotalHT]);
  const discountAmount = useMemo(() => {
    const pct = parseFloat(form.discount_pct || '0') || 0;
    return subtotalHT * pct / 100;
  }, [subtotalHT, form.discount_pct]);
  const totalHT = useMemo(() => Math.max(0, subtotalHT - discountAmount), [subtotalHT, discountAmount]);
  const totalTTC = useMemo(() => baseRentalHT * 1.2 + prestationsTotalTTC - discountAmount, [baseRentalHT, prestationsTotalTTC, discountAmount]);
  const totalPayments = useMemo(() => form.payments.reduce((sum: number, p: any) => sum + (parseFloat(p.amount) || 0), 0), [form.payments]);
  const resteAPayer = useMemo(() => Math.max(0, totalTTC - totalPayments), [totalTTC, totalPayments]);

  const baseRateLoadTokenRef = useRef(0);
  const offreLibrePrestationsLoadTokenRef = useRef(0);

  // Resolve daily HT from pack seasonal rates: average over each calendar day of the rental
  // (per-day seasonal band × tier by total rental duration — Keycars-style).
  useEffect(() => {
    const packIdRaw = form.pricing_pack_id;
    const packId = typeof packIdRaw === 'string' ? Number(packIdRaw) : (packIdRaw as number);

    if (!packId || form.pack_name === 'Offre libre') {
      setLoadingBaseRate(false);
      return;
    }
    if (!form.pickup_datetime || !form.return_datetime) {
      setLoadingBaseRate(false);
      return;
    }
    if (!computedDuration || computedDuration < 1) {
      setLoadingBaseRate(false);
      return;
    }

    const token = ++baseRateLoadTokenRef.current;
    const rentalDaysForTier = computedDuration;
    // Pack default_rate is stored in standard_rate_per_day when the pack is selected.
    // Use it as the per-day fallback when no seasonal rate covers a given day.
    const packDefaultRate = Number(form.standard_rate_per_day || 0) || 0;
    const dayStrings = eachCalendarDayYmd(form.pickup_datetime, form.return_datetime);
    setLoadingBaseRate(true);

    pricingService.getRates({ pack_id: packId })
      .then((res) => {
        if (baseRateLoadTokenRef.current !== token) return;
        const rates: any[] = res.data.data || [];

        const normalizeDate = (d: any) => String(d || '').slice(0, 10);

        let sum = 0;
        for (const day of dayStrings) {
          // Only look at seasonal rates whose date window covers this specific day.
          const periodRates = rates.filter((r) => {
            const start = normalizeDate(r.start_date);
            const end = normalizeDate(r.end_date);
            return day >= start && day <= end;
          });
          const match = periodRates.find((r) => {
            const tier = r.tier;
            if (!tier) return false;
            return rentalDaysForTier >= tier.min_days && rentalDaysForTier <= tier.max_days;
          });
          // If no seasonal rate covers this day, fall back to the pack's default rate.
          sum += match && match.price_ht != null ? Number(match.price_ht) : packDefaultRate;
        }

        const resolvedDailyRate = dayStrings.length > 0 ? sum / dayStrings.length : packDefaultRate;

        setForm((f) => {
          if (f.pack_name === 'Offre libre') return f;
          if (Math.abs((f.daily_rate || 0) - resolvedDailyRate) < 0.00001) return f;
          return { ...f, daily_rate: resolvedDailyRate };
        });
        setLoadingBaseRate(false);
      })
      .catch(() => {
        if (baseRateLoadTokenRef.current === token) setLoadingBaseRate(false);
      });
  }, [form.pricing_pack_id, form.pack_name, form.pickup_datetime, form.return_datetime, form.standard_rate_per_day, computedDuration, pricingService]);

  // Offre libre: preload unit prices from the pricing plan.
  // Users can add/remove prestations, but cannot edit the per-prestation price (unit_rate).
  useEffect(() => {
    if (!isOffreLibre) return;
    if (!form.pricing_plan_id) return;
    if (!selectedVehicle?.vehicle_category_id) return;
    if (!computedDuration || computedDuration < 1) return;
    // Important: do not auto-add prestations when selecting "Offre libre".
    // Only update pricing for rows the user already added in the table.
    if (form.prestations.length === 0) return;

    const token = ++offreLibrePrestationsLoadTokenRef.current;
    const prestationById = new Map(form.prestations.map((p: any) => [p.prestation_id, p]));
    const prestationIds = form.prestations.map((r: any) => r.prestation_id).filter(Boolean);
    if (prestationIds.length === 0) return;

    const params: any = {
      pricing_plan_id: Number(form.pricing_plan_id),
      vehicle_category_id: selectedVehicle.vehicle_category_id,
      rental_days: computedDuration,
      prestation_ids: prestationIds,
    };

    prestationResolveService.resolveRates(params).then((res) => {
      if (offreLibrePrestationsLoadTokenRef.current !== token) return;

      const rateData: Array<{ prestation_id: number; rate_ht: number | null }> = res.data.data || [];
      const rateMap = new Map(rateData.map((r) => [r.prestation_id, r.rate_ht]));

      setForm((f) => {
        if (!isOffreLibre) return f;
        const nextPrestations = f.prestations.map((rp: any) => {
          const hasRate = rateMap.has(rp.prestation_id);
          const nextRate = hasRate ? Number(rateMap.get(rp.prestation_id) ?? 0) : (rp.unit_rate ?? 0);
          const tva = rp.tax_rate ?? 20;
          const nextTotal = (rp.quantity ?? 1) * nextRate * (1 + tva / 100);

          const sameUnit = Math.abs((rp.unit_rate ?? 0) - nextRate) < 0.000001;
          const sameTotal = Math.abs((rp.total_amount ?? 0) - nextTotal) < 0.0001;
          return (sameUnit && sameTotal) ? rp : { ...rp, unit_rate: nextRate, total_amount: nextTotal };
        });

        // Avoid state churn if nothing changed (prevents effect loops).
        const unchanged = nextPrestations.every((rp: any, i: number) => rp === f.prestations[i]);
        return unchanged ? f : { ...f, prestations: nextPrestations };
      });
    }).catch(() => {});
  }, [isOffreLibre, form.pricing_plan_id, selectedVehicle?.vehicle_category_id, computedDuration, form.prestations]);

  const buildAvailabilityParams = useCallback((): ReservationAvailabilityParams | null => {
    if (!form.pickup_datetime || !form.return_datetime || !form.pickup_agency_id || !form.return_agency_id) return null;
    const params: ReservationAvailabilityParams = {
      pickup_date: form.pickup_datetime.split('T')[0],
      return_date: form.return_datetime.split('T')[0],
      pickup_agency_id: Number(form.pickup_agency_id),
      return_agency_id: Number(form.return_agency_id),
    };
    if (selectedVehicleTypeIds.length > 0) params.vehicle_type_ids = [...selectedVehicleTypeIds];
    if (selectedTransmissions.length === 1) {
      params.transmission = selectedTransmissions[0] as 'automatic' | 'manual';
    }
    return params;
  }, [form.pickup_datetime, form.return_datetime, form.pickup_agency_id, form.return_agency_id, selectedVehicleTypeIds, selectedTransmissions]);

  const handleSearchAvailability = useCallback(async () => {
    const params = buildAvailabilityParams();
    if (!params) {
      setError('Veuillez remplir les dates/heures et les agences départ/retour.');
      return;
    }
    setLoadingAvailability(true);
    setError(null);
    try {
      const res = await reservationService.getAvailability(params);
      setCategories(res.data.data?.categories ?? []);
      setAvailabilityLoaded(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la recherche de disponibilités.');
      setCategories([]);
      setAvailabilityLoaded(false);
    } finally {
      setLoadingAvailability(false);
    }
  }, [buildAvailabilityParams]);

  useEffect(() => {
    if (!availabilityLoaded) return;
    const params = buildAvailabilityParams();
    if (!params) return;
    const t = window.setTimeout(() => {
      setLoadingAvailability(true);
      setError(null);
      reservationService
        .getAvailability(params)
        .then((res) => setCategories(res.data.data?.categories ?? []))
        .catch((err: any) => {
          setError(err.response?.data?.message || 'Erreur lors de la recherche de disponibilités.');
          setCategories([]);
        })
        .finally(() => setLoadingAvailability(false));
    }, 350);
    return () => window.clearTimeout(t);
  }, [selectedVehicleTypeIds, selectedTransmissions, availabilityLoaded, buildAvailabilityParams]);

  const handleSearchClients = useCallback(async () => {
    setLoadingClientSearch(true);
    try {
      const res = await subClientService.getAll({ search: clientSearch.trim() || undefined });
      setClientSearchResults(res.data.data || []);
    } catch { setClientSearchResults([]); }
    finally { setLoadingClientSearch(false); }
  }, [clientSearch]);

  const selectSubClient = (sc: SubClient) => {
    setSelectedExistingClient(sc);
    setClientModified(false);
    setDuplicateEmailClient(null);
    setDuplicatePhoneClient(null);
    setForm((f) => ({
      ...f,
      sub_client_id: sc.id,
      customer_type: sc.type || 'individual',
      customer_civility: '',
      customer_first_name: sc.prenom || '',
      customer_last_name: sc.nom || sc.name || '',
      customer_email: sc.email || '',
      customer_phone: sc.phone || '',
      customer_mobile: '',
      customer_company: sc.company_name || '',
      customer_siret: sc.siret || '',
      customer_address: sc.address || '',
      customer_address_complement: '',
      customer_postal_code: sc.postal_code || '',
      customer_city: sc.city || '',
      customer_country: sc.country || '',
      customer_birth_date: '',
      customer_birth_place: '',
    }));
    setClientSearchDialogOpen(false);
    setClientSearchResults([]);
  };

  const clearSelectedClient = () => {
    setSelectedExistingClient(null);
    setClientModified(false);
    setDuplicateEmailClient(null);
    setDuplicatePhoneClient(null);
    setForm((f) => ({
      ...f, sub_client_id: '', customer_civility: '', customer_type: 'individual',
      customer_first_name: '', customer_last_name: '', customer_email: '', customer_phone: '',
      customer_mobile: '', customer_company: '', customer_siret: '', customer_address: '',
      customer_address_complement: '', customer_postal_code: '', customer_city: '', customer_country: '',
      customer_birth_date: '', customer_birth_place: '', customer_accept_newsletter: false, customer_is_active: true,
    }));
  };

  const handleClientFieldChange = (field: string, value: any) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (selectedExistingClient) setClientModified(true);
  };

  const checkDuplicateEmailValue = async (emailValue: string) => {
    if (selectedExistingClient) return;
    const email = (emailValue || '').trim();
    if (!email) {
      setDuplicateEmailClient(null);
      return;
    }
    try {
      const res = await subClientService.getAll({ search: email });
      const exact = (res.data.data || []).find((c: SubClient) => c.email?.toLowerCase() === email.toLowerCase());
      setDuplicateEmailClient(exact ?? null);
    } catch {
      setDuplicateEmailClient(null);
    }
  };

  const checkDuplicatePhoneValue = async (phoneValue: string) => {
    if (selectedExistingClient) return;
    const digits = normalizePhoneDigits(phoneValue || '');
    if (!digits) {
      setDuplicatePhoneClient(null);
      return;
    }
    try {
      const res = await subClientService.getAll({ search: (phoneValue || '').trim() });
      const exact = (res.data.data || []).find((c: SubClient) => c.phone && normalizePhoneDigits(c.phone) === digits);
      setDuplicatePhoneClient(exact ?? null);
    } catch {
      setDuplicatePhoneClient(null);
    }
  };

  const openOfferModal = () => {
    if (form.pricing_plan_id && selectedVehicle?.vehicle_category_id != null) {
      pricingService
        .getPacks({
          plan_id: Number(form.pricing_plan_id),
          category_id: Number(selectedVehicle.vehicle_category_id),
        })
        .then((res) => setPacks(res.data.data || []))
        .catch(() => setPacks([]));
    } else {
      setPacks([]);
    }
    setOfferModalOpen(true);
  };

  const selectPack = (pack: PricingPack) => {
    prestationsLoadTokenRef.current += 1;
    const token = prestationsLoadTokenRef.current;
    setForm((f) => ({
      ...f,
      pricing_pack_id: pack.id,
      pack_name: pack.name,
      franchise_amount: pack.franchise_amount != null ? String(pack.franchise_amount) : '',
      franchise_supplement_per_day: pack.franchise_supplement != null ? String(pack.franchise_supplement) : '',
      standard_rate_per_day: pack.default_rate != null ? String(pack.default_rate) : '',
      daily_rate: Number(pack.default_rate ?? 0) || 0,
    }));
    pricingService.getPackPrestations(pack.id).then((res) => {
      if (prestationsLoadTokenRef.current !== token) return; // stale response
      const included = (res.data.data || res.data || []) as any[];
      const rows = Array.isArray(included) ? included.map((p: any) => ({
        prestation_id: p.id ?? p.prestation_id, name: p.name ?? '', is_included: true,
        pricing_mode: p.pricing_mode || 'flat', unit_rate: p.unit_rate ?? 0, tax_rate: p.tax_rate ?? 20, quantity: 1,
        total_amount: p.unit_rate ?? 0,
      })) : [];
      setForm((f) => ({ ...f, prestations: rows }));
    }).catch(() => {});
    setOfferModalOpen(false);
  };

  const selectOffreLibre = () => {
    prestationsLoadTokenRef.current += 1; // invalidate any in-flight pack prestations load
    setForm((f) => ({
      ...f,
      pricing_pack_id: '' as any,
      pack_name: 'Offre libre',
      franchise_amount: '',
      franchise_supplement_per_day: '',
      // Offre libre uses the selected category "tarif standard" as starting daily rate.
      daily_rate: (() => {
        const standardRate = categories.find(
          (c) => c.vehicle_category_id === selectedVehicle?.vehicle_category_id
        )?.rate_ht;
        return standardRate != null ? Number(standardRate) : 0;
      })(),
      standard_rate_per_day: (() => {
        const standardRate = categories.find(
          (c) => c.vehicle_category_id === selectedVehicle?.vehicle_category_id
        )?.rate_ht;
        return standardRate != null ? String(standardRate) : '';
      })(),
      prestations: [],
    }));
    setOfferModalOpen(false);
  };

  const addPrestationRow = async (prestation?: Prestation) => {
    const p = prestation || prestations[0];
    if (!p) return;

    let defaultRate = 0;
    if (form.pricing_plan_id && selectedVehicle?.vehicle_category_id && computedDuration > 0) {
      try {
        const res = await prestationResolveService.resolveRates({
          pricing_plan_id: Number(form.pricing_plan_id),
          vehicle_category_id: selectedVehicle.vehicle_category_id,
          rental_days: computedDuration,
          prestation_ids: [p.id],
        });
        const resolved = res.data.data?.find((r: any) => r.prestation_id === p.id);
        if (resolved?.rate_ht != null) defaultRate = resolved.rate_ht;
      } catch { /* fallback to 0 */ }
    }

    const tva = p.tax_rate ?? 20;
    setForm((f) => ({
      ...f,
      prestations: [...f.prestations, {
        prestation_id: p.id, name: p.name, is_included: false, pricing_mode: p.pricing_mode || 'flat',
        unit_rate: defaultRate, tax_rate: tva, quantity: 1, total_amount: defaultRate * (1 + tva / 100),
      }],
    }));
  };

  const removePrestationRow = (index: number) => setForm((f) => ({ ...f, prestations: f.prestations.filter((_, i) => i !== index) }));

  const updatePrestationRow = (index: number, field: string, value: any) => {
    setForm((f) => {
      const next = [...f.prestations];
      if (!next[index]) return f;
      (next[index] as any)[field] = value;
      if (field === 'quantity' || field === 'unit_rate') {
        const tva = next[index].tax_rate ?? 20;
        next[index].total_amount = (next[index].quantity ?? 1) * (next[index].unit_rate ?? 0) * (1 + tva / 100);
      }
      return { ...f, prestations: next };
    });
  };

  const openVehicleModal = (cat: AvailabilityCategory) => {
    setVehicleSearchText('');
    setCategoryForModal(cat);
    setVehicleModalOpen(true);
    // Re-fetch availability when opening the modal so stop sells / reservations changed elsewhere apply
    if (form.pickup_datetime && form.return_datetime && form.pickup_agency_id && form.return_agency_id) {
      (async () => {
        try {
          const params = buildAvailabilityParams();
          if (!params) return;
          const res = await reservationService.getAvailability(params);
          const fresh = res.data.data?.categories ?? [];
          setCategories(fresh);
          const updated = fresh.find((c) => c.vehicle_category_id === cat.vehicle_category_id);
          if (updated) setCategoryForModal(updated);
        } catch {
          /* keep modal state */
        }
      })();
    }
  };

  const selectVehicle = (v: any) => {
    const prevCat = selectedVehicle?.vehicle_category_id;
    const newCat = v.vehicle_category_id;
    const categoryChanged =
      prevCat != null &&
      newCat != null &&
      Number(prevCat) !== Number(newCat);

    setSelectedVehicle({ ...v, category_name: categoryForModal?.category_name || v.category_name || '' });
    setForm((f) => {
      const next: typeof f = { ...f, vehicle_id: v.id };
      if (!categoryChanged) return next;

      if (f.pack_name === 'Offre libre') {
        const standardRate = categories.find((c) => c.vehicle_category_id === newCat)?.rate_ht;
        return {
          ...next,
          daily_rate: standardRate != null ? Number(standardRate) : 0,
          standard_rate_per_day: standardRate != null ? String(standardRate) : '',
        };
      }

      return {
        ...next,
        pricing_pack_id: '' as number | '',
        pack_name: '',
        franchise_amount: '',
        franchise_supplement_per_day: '',
        standard_rate_per_day: '',
        daily_rate: 0,
        prestations: [],
      };
    });
    setVehicleModalOpen(false);
    setCategoryForModal(null);
  };

  const requestVehicleSelection = (v: AvailabilityVehicleItem) => {
    if (Boolean(v.is_stop_sell_blocked)) {
      setPendingStopSellVehicle(v);
      setStopSellForceOpen(true);
    } else {
      selectVehicle(v);
    }
  };

  const confirmStopSellSelection = () => {
    if (pendingStopSellVehicle) {
      selectVehicle(pendingStopSellVehicle);
    }
    setPendingStopSellVehicle(null);
    setStopSellForceOpen(false);
  };

  const addPaymentRow = () => {
    setForm((f) => ({
      ...f,
      payments: [...f.payments, {
        type: 'reglement', mode: '', label: '', amount: '', payment_date: '', due_date: '',
        agency_id: currentUser.agency_id || '',
        agent_name: currentUser.name || '',
      }],
    }));
  };

  const updatePaymentRow = (index: number, field: string, value: any) => {
    setForm((f) => {
      const next = [...f.payments];
      if (!next[index]) return f;
      (next[index] as any)[field] = value;
      return { ...f, payments: next };
    });
  };

  const removePaymentRow = (index: number) => setForm((f) => ({ ...f, payments: f.payments.filter((_, i) => i !== index) }));

  const handleNext = () => {
    if (currentStepId === 'client') {
      const isNew = !selectedExistingClient;
      const isModified = selectedExistingClient && clientModified;
      if (isNew || isModified) {
        setClientConfirmDialogOpen(true);
        return;
      }
    }
    if (step < currentSteps.length - 1) setStep(step + 1);
  };

  const handlePrev = () => { if (step > 0) setStep(step - 1); };

  const handleDownloadPdf = async () => {
    if (!id) return;
    try {
      const res = await reservationService.downloadPdf(Number(id));
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `reservation-${id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Erreur lors du téléchargement du PDF.');
    }
  };

  const handleAddComment = async () => {
    if (!id || !newCommentText.trim()) return;
    try {
      const res = await reservationService.addComment(Number(id), { content: newCommentText.trim() });
      setComments((prev) => [res.data.data, ...prev]);
      setNewCommentText('');
    } catch {
      setError("Erreur lors de l'ajout du commentaire.");
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!id) return;
    try {
      await reservationService.deleteComment(Number(id), commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch {
      setError('Erreur lors de la suppression du commentaire.');
    }
  };

  const handleSendBonDeCommande = async () => {
    if (!id) return;
    try {
      setSendingBonLoading(true);
      setError(null);
      await reservationService.sendBonDeCommande(Number(id));
      const res = await reservationService.getMailings(Number(id));
      setMailings(res.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || "Erreur lors de l'envoi du bon de commande.");
    } finally {
      setSendingBonLoading(false);
    }
  };

  const handleSendContract = async () => {
    if (!id) return;
    try {
      setSendingContractLoading(true);
      setError(null);
      await reservationService.sendContract(Number(id));
      const res = await reservationService.getMailings(Number(id));
      setMailings(res.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || "Erreur lors de l'envoi du contrat.");
    } finally {
      setSendingContractLoading(false);
    }
  };

  const openRelanceDialog = () => {
    const ref = (form.reservation_reference || '').trim();
    setRelanceSubject(
      id ? (ref ? `Message — réservation N° ${ref}` : `Message — réservation N° ${id}`) : 'Message'
    );
    setRelanceBody('');
    setRelanceSentTo((form.customer_email || '').trim());
    setRelanceDialogOpen(true);
  };

  const handleSendRelance = async () => {
    if (!id || !relanceSubject.trim() || !relanceBody.trim()) return;
    try {
      setSendingRelance(true);
      setError(null);
      const payload: { subject: string; body: string; sent_to?: string } = {
        subject: relanceSubject.trim(),
        body: relanceBody.trim(),
      };
      if (relanceSentTo.trim()) payload.sent_to = relanceSentTo.trim();
      await reservationService.sendRelance(Number(id), payload);
      const res = await reservationService.getMailings(Number(id));
      setMailings(res.data.data || []);
      setRelanceDialogOpen(false);
      setRelanceBody('');
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Erreur lors de l'envoi du message."
      );
    } finally {
      setSendingRelance(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.vehicle_id || !form.pickup_agency_id || !form.return_agency_id || !form.pickup_datetime || !form.return_datetime) {
      setError('Veuillez compléter les étapes Dates & Véhicule.');
      return;
    }
    setSubmitLoading(true);
    setError(null);
    try {
      let subClientId = form.sub_client_id;
      if (!selectedExistingClient && form.customer_last_name && form.customer_first_name && form.customer_email) {
        const res = await subClientService.create({
          nom: form.customer_last_name, prenom: form.customer_first_name, email: form.customer_email,
          phone: form.customer_phone || form.customer_mobile || undefined, type: form.customer_type,
          company_name: form.customer_company || undefined, siret: form.customer_siret || undefined,
          address: form.customer_address || undefined, city: form.customer_city || undefined,
          postal_code: form.customer_postal_code || undefined, country: form.customer_country || undefined,
          is_active: form.customer_is_active,
        });
        subClientId = res.data.data.id;
      } else if (selectedExistingClient && clientModified) {
        await subClientService.update(selectedExistingClient.id, {
          nom: form.customer_last_name, prenom: form.customer_first_name, email: form.customer_email,
          phone: form.customer_phone || undefined, type: form.customer_type,
          company_name: form.customer_company || undefined, address: form.customer_address || undefined,
          city: form.customer_city || undefined, postal_code: form.customer_postal_code || undefined,
          country: form.customer_country || undefined,
        });
      }

      const autoStatus = form.payments.length > 0 ? 'confirmed' : 'pending';

      const payload: any = {
        vehicle_id: form.vehicle_id,
        pickup_agency_id: form.pickup_agency_id,
        return_agency_id: form.return_agency_id,
        pickup_datetime: new Date(form.pickup_datetime).toISOString(),
        return_datetime: new Date(form.return_datetime).toISOString(),
        status: autoStatus,
        daily_rate: form.daily_rate || 0,
        base_rental_amount: baseRentalHT,
        prestations_amount: prestationsTotalHT,
        subtotal: subtotalHT,
        discount_amount: discountAmount,
        total_amount: totalTTC,
        franchise_amount: form.franchise_amount ? parseFloat(form.franchise_amount) : 0,
      };
      if (subClientId) payload.sub_client_id = subClientId;
      if (form.customer_civility) payload.customer_civility = form.customer_civility;
      if (form.customer_first_name) payload.customer_first_name = form.customer_first_name;
      if (form.customer_last_name) payload.customer_last_name = form.customer_last_name;
      if (form.customer_email) payload.customer_email = form.customer_email;
      if (form.customer_phone) payload.customer_phone = form.customer_phone;
      if (form.customer_company) payload.customer_company = form.customer_company;
      if (form.customer_address) payload.customer_address = form.customer_address;
      if (form.customer_city) payload.customer_city = form.customer_city;
      if (form.customer_postal_code) payload.customer_postal_code = form.customer_postal_code;
      if (form.customer_country) payload.customer_country = form.customer_country;
      if (form.customer_birth_date) payload.customer_birth_date = form.customer_birth_date;
      if (form.customer_birth_place) payload.customer_birth_place = form.customer_birth_place;
      if (form.pricing_plan_id) payload.pricing_plan_id = form.pricing_plan_id;
      if (form.pricing_pack_id) payload.pricing_pack_id = form.pricing_pack_id;
      if (form.pack_name) payload.pack_name = form.pack_name;
      if (form.prestations?.length) payload.prestations = form.prestations.map((p) => ({
        prestation_id: p.prestation_id ?? p.id, name: p.name, is_included: p.is_included ?? false,
        pricing_mode: p.pricing_mode ?? 'flat', unit_rate: p.unit_rate ?? 0, quantity: p.quantity ?? 1, total_amount: p.total_amount ?? 0,
      }));

      const prestationStockLines = (form.prestations || [])
        .map((p: any) => ({ prestation_id: p.prestation_id ?? p.id, quantity: p.quantity ?? 1 }))
        .filter((l) => l.prestation_id && l.quantity > 0);
      if (prestationStockLines.length > 0 && !stockWarningsAcknowledged) {
        const stockRes = await reservationService.checkPrestationStock({
          pickup_datetime: new Date(form.pickup_datetime).toISOString(),
          return_datetime: new Date(form.return_datetime).toISOString(),
          ...(isEdit && id ? { exclude_reservation_id: Number(id) } : {}),
          prestations: prestationStockLines,
        });
        const warnings = (stockRes.data as any)?.data?.warnings ?? [];
        if (warnings.length > 0) {
          setStockWarnings(warnings);
          setSubmitLoading(false);
          return;
        }
      }

      if (form.payments?.length) {
        payload.payments = form.payments.map((p: any) => ({
          type: p.type || 'reglement',
          mode: p.mode || '',
          label: p.label || '',
          amount: parseFloat(p.amount) || 0,
          payment_date: p.payment_date || null,
          due_date: p.due_date || null,
          agency_id: p.agency_id || null,
          agent_name: p.agent_name || '',
        }));
      }

      if (isEdit) {
        await reservationService.update(Number(id), payload);
      } else {
        const res = await reservationService.create(payload);
        const newId = (res.data as any)?.data?.id;
        if (newId && pendingCreateComment.trim()) {
          try {
            await reservationService.addComment(Number(newId), { content: pendingCreateComment.trim() });
          } catch {
            /* comment non-bloquant */
          }
        }
      }
      setStockWarnings([]);
      setStockWarningsAcknowledged(false);
      navigate('/reservations');
    } catch (err: any) {
      setError(err.response?.data?.message || "Erreur lors de l'enregistrement.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const selectedCategoryName = useMemo(() => {
    if (!selectedVehicle) return '';
    return selectedVehicle.category_name || `Cat. ${selectedVehicle.vehicle_category_id}`;
  }, [selectedVehicle]);

  const filteredModalVehicles = useMemo(() => {
    if (!categoryForModal) return [];
    const search = vehicleSearchText.toLowerCase().trim();
    if (!search) return categoryForModal.vehicles;
    return categoryForModal.vehicles.filter((v: any) =>
      (v.marque || '').toLowerCase().includes(search) ||
      (v.modele || '').toLowerCase().includes(search) ||
      (v.registration_number || '').toLowerCase().includes(search)
    );
  }, [categoryForModal, vehicleSearchText]);

  const tarifStepIndex = currentSteps.findIndex((s) => s.id === 'tarif');

  const pickupAgencyName = useMemo(() => agencies.find((a) => a.id === form.pickup_agency_id)?.name || '—', [agencies, form.pickup_agency_id]);
  const returnAgencyName = useMemo(() => agencies.find((a) => a.id === form.return_agency_id)?.name || '—', [agencies, form.return_agency_id]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f6fa' }}>
      {/* TOP NAVIGATION BAR (sticky) */}
      <Box sx={{ position: 'sticky', top: 0, bgcolor: 'white', borderBottom: '1px solid #e0e4ea', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', zIndex: 100 }}>
        <Box sx={{ mx: 'auto', px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Tabs
            value={step}
            onChange={(_, v) => setStep(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 42,
              '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', color: '#78909c', '&.Mui-selected': { color: '#2c3e50', fontWeight: 700 } },
              '& .MuiTabs-indicator': { bgcolor: '#2c3e50', height: 3, borderRadius: '3px 3px 0 0' },
            }}
          >
            {currentSteps.map((s) => <Tab key={s.id} icon={s.icon} iconPosition="start" label={s.label} />)}
          </Tabs>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexShrink: 0 }}>
            <Button variant="outlined" size="small" onClick={() => navigate('/reservations')} sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#ccc', color: '#666' }}>Annuler</Button>
            {step > 0 && <Button variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={handlePrev} sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#2c3e50', color: '#2c3e50' }}>Précédent</Button>}
            {step < currentSteps.length - 1 ? (
              <Button variant="contained" size="small" endIcon={<ArrowForwardIcon />} onClick={handleNext} sx={{ borderRadius: 2, px: 2.5, textTransform: 'none', fontWeight: 700, bgcolor: '#2c3e50', '&:hover': { bgcolor: '#34495e' } }}>Suivant</Button>
            ) : (
              <Button variant="contained" size="small" onClick={handleSubmit} disabled={submitLoading} sx={{ borderRadius: 2, px: 2.5, textTransform: 'none', fontWeight: 700, bgcolor: '#27ae60', '&:hover': { bgcolor: '#219a52' } }}>
                {submitLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Enregistrer'}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {error && <Box sx={{ mx: 'auto', px: 2, pt: 2 }}><Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>{error}</Alert></Box>}

      {stockWarnings.length > 0 && (
        <Box sx={{ mx: 'auto', px: 2, pt: 2 }}>
          <Alert
            severity="warning"
            sx={{ borderRadius: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                sx={{ whiteSpace: 'nowrap', fontWeight: 700, textTransform: 'none' }}
                onClick={() => { setStockWarningsAcknowledged(true); setStockWarnings([]); handleSubmit(); }}
              >
                Confirmer quand même
              </Button>
            }
          >
            <strong>Stock théorique insuffisant sur ces dates :</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {stockWarnings.map((w: any, i: number) => (
                <li key={i}>
                  {w.name} — demandé&nbsp;<strong>{w.requested}</strong>, disponible&nbsp;<strong>{w.available_for_period}</strong> (stock total&nbsp;{w.stock_quantity}, engagé&nbsp;{w.allocated_overlapping})
                </li>
              ))}
            </ul>
          </Alert>
        </Box>
      )}

      {/* DATES & VEHICULE */}
      <Fade in={currentStepId === 'dates_vehicle'} timeout={300} mountOnEnter unmountOnExit>
        <Box>
          <Box sx={{ position: 'relative', minHeight: { xs: 280, md: 340 }, backgroundImage: `linear-gradient(135deg, rgba(44,62,80,0.92) 0%, rgba(52,73,94,0.85) 100%), url(${HERO_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2, py: 2 }}>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
              {isEdit
                ? `Réservation N° ${form.reservation_reference?.trim() || '—'}`
                : 'Nouvelle réservation'}
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', mb: 2 }}>Trouvez le véhicule idéal pour votre client</Typography>
            <Paper elevation={8} sx={{ p: 2.5, borderRadius: 3, width: '100%', maxWidth: 'min(96vw, 1480px)', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
              <Grid container spacing={2} alignItems="center">
                {/* Row 1 (md+): full width for dates/times — avoids md:1 columns that clip values */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField fullWidth size="medium" label="Date départ" type="date" value={form.pickup_datetime ? form.pickup_datetime.slice(0, 10) : ''} onChange={(e) => { const t = form.pickup_datetime ? form.pickup_datetime.slice(11, 16) || '08:00' : '08:00'; setForm((f) => ({ ...f, pickup_datetime: e.target.value ? `${e.target.value}T${t}` : '' })); }} InputLabelProps={{ shrink: true }} InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ fontSize: 20, color: 'text.secondary' }} /></InputAdornment> }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafbfc' } }} />
                </Grid>
                <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                  <TextField fullWidth size="medium" label="Heure départ" type="time" value={form.pickup_datetime ? form.pickup_datetime.slice(11, 16) : ''} onChange={(e) => { const d = form.pickup_datetime ? form.pickup_datetime.slice(0, 10) : ''; setForm((f) => ({ ...f, pickup_datetime: d ? `${d}T${e.target.value}` : '' })); }} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafbfc' } }} />
                </Grid>
                <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                  <TextField fullWidth size="medium" label="Date retour" type="date" value={form.return_datetime ? form.return_datetime.slice(0, 10) : ''} onChange={(e) => { const t = form.return_datetime ? form.return_datetime.slice(11, 16) || '18:00' : '18:00'; setForm((f) => ({ ...f, return_datetime: e.target.value ? `${e.target.value}T${t}` : '' })); }} InputLabelProps={{ shrink: true }} InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ fontSize: 20, color: 'text.secondary' }} /></InputAdornment> }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafbfc' } }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField fullWidth size="medium" label="Heure retour" type="time" value={form.return_datetime ? form.return_datetime.slice(11, 16) : ''} onChange={(e) => { const d = form.return_datetime ? form.return_datetime.slice(0, 10) : ''; setForm((f) => ({ ...f, return_datetime: d ? `${d}T${e.target.value}` : '' })); }} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafbfc' } }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                  <FormControl fullWidth size="medium">
                    <InputLabel>Agence départ</InputLabel>
                    <Select value={form.pickup_agency_id} label="Agence départ" onChange={(e) => setForm((f) => ({ ...f, pickup_agency_id: e.target.value as number }))} sx={{ borderRadius: 2, bgcolor: '#fafbfc' }} startAdornment={<InputAdornment position="start"><LocationOnIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>}>
                      {agencies.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth size="medium">
                    <InputLabel>Agence retour</InputLabel>
                    <Select value={form.return_agency_id} label="Agence retour" onChange={(e) => setForm((f) => ({ ...f, return_agency_id: e.target.value as number }))} sx={{ borderRadius: 2, bgcolor: '#fafbfc' }} startAdornment={<InputAdornment position="start"><LocationOnIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>}>
                      {agencies.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 2 }}>
                  <Button variant="contained" fullWidth onClick={handleSearchAvailability} disabled={loadingAvailability} sx={{ borderRadius: 2, py: 1.25, minHeight: 56, textTransform: 'none', fontWeight: 700, bgcolor: '#2c3e50', '&:hover': { bgcolor: '#34495e' }, boxShadow: '0 4px 14px rgba(44,62,80,0.35)' }}>
                    {loadingAvailability ? <CircularProgress size={22} sx={{ color: 'white' }} /> : <SearchIcon />}
                  </Button>
                </Grid>
              </Grid>
              {computedDuration > 0 && <Typography variant="body2" sx={{ mt: 1.5, textAlign: 'center', fontWeight: 600, color: '#2c3e50' }}>Durée estimée: {computedDuration} jour(s)</Typography>}
            </Paper>
          </Box>

          {/* Vehicle Type Filters */}
          <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e8ecf1', py: 2, px: 2 }}>
            <Box sx={{ mx: 'auto' }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>Types de véhicules</Typography>
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Card variant="outlined" onClick={() => setSelectedVehicleTypeIds([])} sx={{ cursor: 'pointer', borderColor: selectedVehicleTypeIds.length === 0 ? '#2c3e50' : undefined, bgcolor: selectedVehicleTypeIds.length === 0 ? 'rgba(44,62,80,0.08)' : '#f0f2f5', borderWidth: selectedVehicleTypeIds.length === 0 ? 2 : 1, borderRadius: 2, overflow: 'hidden', transition: 'all 0.2s', '&:hover': { borderColor: '#2c3e50', transform: 'translateY(-2px)' } }}>
                    <Box sx={{ height: 88, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <DirectionsCarIcon sx={{ color: '#2c3e50', fontSize: 28, mb: 0.5 }} />
                      <Typography variant="body2" fontWeight={600}>Tous</Typography>
                    </Box>
                  </Card>
                </Grid>
                {vehicleTypes.map((vt) => (
                  <Grid size={{ xs: 6, sm: 3 }} key={vt.id}>
                    <Card variant="outlined" onClick={() => { if (selectedVehicleTypeIds.includes(vt.id)) setSelectedVehicleTypeIds((p) => p.filter((x) => x !== vt.id)); else setSelectedVehicleTypeIds((p) => [...p, vt.id]); }} sx={{ cursor: 'pointer', borderColor: selectedVehicleTypeIds.includes(vt.id) ? '#2c3e50' : undefined, borderWidth: selectedVehicleTypeIds.includes(vt.id) ? 2 : 1, borderRadius: 2, overflow: 'hidden', transition: 'all 0.2s', '&:hover': { borderColor: '#2c3e50', transform: 'translateY(-2px)' } }}>
                      <Box sx={{ position: 'relative', height: 88, bgcolor: (vt as any).image_url ? undefined : '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {(vt as any).image_url ? (
                          <Box component="img" src={(vt as any).image_url} alt={vt.name} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <DirectionsCarIcon sx={{ color: '#78909c', fontSize: 36 }} />
                        )}
                        <Typography variant="body2" fontWeight={700} sx={{ position: 'relative', zIndex: 1, color: (vt as any).image_url ? 'white' : 'text.primary', textShadow: (vt as any).image_url ? '0 1px 2px rgba(0,0,0,0.8)' : 'none', px: 1, textAlign: 'center' }}>{vt.name}</Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ display: 'flex', gap: 2, mt: 1.5, alignItems: 'center' }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase' }}>Transmission</Typography>
                {['automatic', 'manual'].map((t) => (
                  <FormControlLabel key={t} control={<Checkbox size="small" checked={selectedTransmissions.includes(t)} onChange={(e) => { if (e.target.checked) setSelectedTransmissions((p) => [...p, t]); else setSelectedTransmissions((p) => p.filter((x) => x !== t)); }} />} label={<Typography variant="body2">{t === 'automatic' ? 'Automatique' : 'Manuelle'}</Typography>} />
                ))}
              </Box>
            </Box>
          </Box>

          {/* Results + Selected Vehicle */}
          <Box sx={{ mx: 'auto', px: 2, py: 3 }}>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                {categories.length > 0 && <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#2c3e50' }}>{categories.reduce((sum, c) => sum + c.available_count, 0)} véhicule(s) disponible(s)</Typography>}
                {loadingAvailability && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#2c3e50' }} /></Box>}
                <Grid container spacing={2.5}>
                  {categories.map((cat) => {
                    const rep = cat.vehicles?.[0];
                    return (
                      <Grid size={{ xs: 12, sm: 6, md: 3 }} key={cat.vehicle_category_id}>
                        <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', transition: 'all 0.2s', cursor: 'pointer', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', borderColor: '#2c3e50' } }} onClick={() => openVehicleModal(cat)}>
                          <Box sx={{ height: 140, bgcolor: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
                            {cat.image_url ? (
                              <Box
                                component="img"
                                src={cat.image_url}
                                alt={cat.category_name}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : rep?.primary_image_url ? (
                              <Box
                                component="img"
                                src={rep.primary_image_url}
                                alt={cat.category_name}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <DirectionsCarIcon sx={{ fontSize: 56, color: '#cfd8dc' }} />
                            )}
                            <Chip label={`${cat.available_count} dispo.`} size="small" sx={{ position: 'absolute', top: 8, right: 8, bgcolor: '#2c3e50', color: 'white', fontWeight: 700, fontSize: '0.7rem' }} />
                          </Box>
                          <CardContent sx={{ p: 1.5 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#2c3e50' }}>{cat.category_name}</Typography>
                            {cat.vehicle_type_name && <Typography variant="caption" color="text.secondary">{cat.vehicle_type_name}</Typography>}
                          </CardContent>
                          <CardActions sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
                            <Button size="small" variant="contained" fullWidth endIcon={<ArrowForwardIcon />} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#2c3e50', '&:hover': { bgcolor: '#34495e' } }}>Choisir</Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
              {selectedVehicle && (
                <Paper elevation={0} sx={{ width: 300, minWidth: 300, borderRadius: 3, border: '2px solid #2c3e50', overflow: 'hidden', position: 'sticky', top: 80 }}>
                  <Box sx={{ bgcolor: '#2c3e50', color: 'white', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}><CheckCircleIcon fontSize="small" /><Typography variant="subtitle2" fontWeight={700}>Véhicule sélectionné</Typography></Box>
                  <Box sx={{ height: 160, bgcolor: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {selectedVehicle.primary_image_url ? <img src={selectedVehicle.primary_image_url} alt={`${selectedVehicle.marque} ${selectedVehicle.modele}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <DirectionsCarIcon sx={{ fontSize: 56, color: '#cfd8dc' }} />}
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#2c3e50' }}>{selectedVehicle.marque} {selectedVehicle.modele}</Typography>
                    {selectedVehicle.category_name && <Typography variant="caption" color="text.secondary">{selectedVehicle.category_name}</Typography>}
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary">Immat: <strong>{selectedVehicle.registration_number}</strong></Typography>
                    {selectedVehicle.maintenance_status && selectedVehicle.maintenance_status !== 'ready' && (
                      <Chip icon={<BuildIcon />} label={selectedVehicle.maintenance_status === 'maintenance_needed' ? 'Maintenance requise' : 'Maintenance à prévoir'} size="small" color="warning" sx={{ mt: 1, fontSize: '0.7rem', display: 'flex' }} />
                    )}
                    {selectedVehicle.mileage != null && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <SpeedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">Kilométrage: <strong>{Number(selectedVehicle.mileage).toLocaleString('fr-FR')} km</strong></Typography>
                      </Box>
                    )}
                    {selectedVehicle.date_derniere_intervention && (
                      <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BuildIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">Dernière intervention: <strong>{new Date(selectedVehicle.date_derniere_intervention).toLocaleDateString('fr-FR')}</strong></Typography>
                      </Box>
                    )}
                    <Button
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => window.open(`/maintenance?vehicle_id=${selectedVehicle.id}&plate=${encodeURIComponent(selectedVehicle.registration_number || '')}`, '_blank')}
                      sx={{ mt: 1, textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', color: '#2c3e50' }}
                    >
                      Voir maintenance
                    </Button>
                  </Box>
                </Paper>
              )}
            </Box>
            {selectedVehicle && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />} onClick={handleNext} sx={{ borderRadius: 3, px: 6, py: 1.5, textTransform: 'none', fontWeight: 700, fontSize: '1rem', bgcolor: '#27ae60', '&:hover': { bgcolor: '#219a52' }, boxShadow: '0 4px 14px rgba(39,174,96,0.3)' }}>Suivant — Étape Client</Button>
              </Box>
            )}
          </Box>
        </Box>
      </Fade>

      {/* CLIENT */}
      <Fade in={currentStepId === 'client'} timeout={300} mountOnEnter unmountOnExit>
        <Box sx={{ mx: 'auto', px: 2, py: 3 }}>
          <Paper elevation={0} sx={{ p: 0, borderRadius: 3, border: '1px solid #e0e4ea', overflow: 'hidden' }}>
            <Box sx={{ bgcolor: '#2c3e50', color: 'white', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1 }}><PersonIcon /><Typography variant="h6" fontWeight={700}>Client</Typography></Box>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField fullWidth size="small" placeholder="nom, prénom, N° Client, téléphone, email" value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { handleSearchClients(); setClientSearchDialogOpen(true); } }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment> }} sx={{ maxWidth: 500, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                <Button variant="contained" onClick={() => { handleSearchClients(); setClientSearchDialogOpen(true); }} disabled={loadingClientSearch} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, bgcolor: '#2c3e50', '&:hover': { bgcolor: '#34495e' } }}>{loadingClientSearch ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Rechercher'}</Button>
                <Button variant="outlined" startIcon={<BadgeIcon />} onClick={() => setIdScannerOpen(true)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#1565c0', color: '#1565c0', '&:hover': { borderColor: '#0d47a1', bgcolor: '#e3f2fd' } }}>
                  Scanner pièce d'identité
                </Button>
              </Box>

              {selectedExistingClient && (
                <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: '#e8f5e9', border: '1px solid #a5d6a7', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircleIcon sx={{ color: '#4caf50' }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#2e7d32' }}>Client existant sélectionné — N° {selectedExistingClient.id}</Typography>
                      <Typography variant="body2" color="text.secondary">{selectedExistingClient.prenom} {selectedExistingClient.nom || selectedExistingClient.name} — {selectedExistingClient.email}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => window.open(`${window.location.origin}/sub-clients/${selectedExistingClient.id}`, '_blank', 'noopener,noreferrer')} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#2e7d32', color: '#2e7d32' }}>Voir la fiche</Button>
                    <Button size="small" variant="outlined" color="error" onClick={clearSelectedClient} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>Nouveau client</Button>
                  </Box>
                </Box>
              )}

              {!selectedExistingClient && duplicateEmailClient && (
                <Alert
                  severity="warning"
                  sx={{ mb: 3 }}
                  action={
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          selectSubClient(duplicateEmailClient);
                        }}
                      >
                        Utiliser ce client
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          setDuplicateEmailClient(null);
                        }}
                      >
                        Continuer quand même
                      </Button>
                    </Box>
                  }
                >
                  Un client avec cet email existe déjà: {subClientDisplayName(duplicateEmailClient)} (N°
                  {duplicateEmailClient.id})
                </Alert>
              )}

              {!selectedExistingClient && duplicatePhoneClient && (
                <Alert
                  severity="warning"
                  sx={{ mb: 3 }}
                  action={
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          selectSubClient(duplicatePhoneClient);
                        }}
                      >
                        Utiliser ce client
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          setDuplicatePhoneClient(null);
                        }}
                      >
                        Continuer quand même
                      </Button>
                    </Box>
                  }
                >
                  Un client avec ce numéro existe déjà: {subClientDisplayName(duplicatePhoneClient)} (N°
                  {duplicatePhoneClient.id})
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#2c3e50' }}>FICHE CLIENT {selectedExistingClient && clientModified && <Chip label="Modifié" size="small" color="warning" sx={{ ml: 1, fontSize: '0.65rem' }} />}</Typography>
                <RadioGroup row value={form.customer_type} onChange={(e) => handleClientFieldChange('customer_type', e.target.value)}>
                  <FormControlLabel value="individual" control={<Radio size="small" />} label={<Typography variant="body2">Particulier</Typography>} />
                  <FormControlLabel value="company" control={<Radio size="small" />} label={<Typography variant="body2">Entreprise</Typography>} />
                </RadioGroup>
              </Box>

              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" fontWeight={700} sx={{ color: '#2c3e50', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5, display: 'block' }}>Général</Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControl fullWidth size="small"><InputLabel>Civilité</InputLabel><Select value={form.customer_civility} label="Civilité" onChange={(e) => handleClientFieldChange('customer_civility', e.target.value)} sx={{ borderRadius: 2 }}><MenuItem value="">—</MenuItem><MenuItem value="M.">M.</MenuItem><MenuItem value="Mme">Mme</MenuItem><MenuItem value="Mlle">Mlle</MenuItem></Select></FormControl>
                    </Grid>
                    {form.customer_type === 'company' && (<><Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth size="small" label="Nom société" value={form.customer_company} onChange={(e) => handleClientFieldChange('customer_company', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid><Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth size="small" label="SIRET" value={form.customer_siret} onChange={(e) => handleClientFieldChange('customer_siret', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid></>)}
                    <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Nom" value={form.customer_last_name} onChange={(e) => handleClientFieldChange('customer_last_name', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Prénom" value={form.customer_first_name} onChange={(e) => handleClientFieldChange('customer_first_name', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Date de naissance" type="date" value={form.customer_birth_date} onChange={(e) => handleClientFieldChange('customer_birth_date', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Lieu de naissance" value={form.customer_birth_place} onChange={(e) => handleClientFieldChange('customer_birth_place', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
                  </Grid>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" fontWeight={700} sx={{ color: '#2c3e50', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5, display: 'block' }}>Coordonnées</Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 8 }}><TextField fullWidth size="small" label="Adresse" value={form.customer_address} onChange={(e) => handleClientFieldChange('customer_address', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
                    <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth size="small" label="Code postal" value={form.customer_postal_code} onChange={(e) => handleClientFieldChange('customer_postal_code', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
                    <Grid size={12}><TextField fullWidth size="small" label="Complément adresse" value={form.customer_address_complement} onChange={(e) => handleClientFieldChange('customer_address_complement', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Ville" value={form.customer_city} onChange={(e) => handleClientFieldChange('customer_city', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Pays" value={form.customer_country} onChange={(e) => handleClientFieldChange('customer_country', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Téléphone"
                        value={form.customer_phone}
                        onChange={(e) => {
                          handleClientFieldChange('customer_phone', e.target.value);
                          setDuplicatePhoneClient(null);
                        }}
                        onBlur={(e) => checkDuplicatePhoneValue((e.target as HTMLInputElement).value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Mobile"
                        value={form.customer_mobile}
                        onChange={(e) => {
                          handleClientFieldChange('customer_mobile', e.target.value);
                          setDuplicatePhoneClient(null);
                        }}
                        onBlur={(e) => checkDuplicatePhoneValue((e.target as HTMLInputElement).value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Email"
                        type="email"
                        value={form.customer_email}
                        onChange={(e) => {
                          handleClientFieldChange('customer_email', e.target.value);
                          setDuplicateEmailClient(null);
                        }}
                        onBlur={(e) => checkDuplicateEmailValue((e.target as HTMLInputElement).value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <FormControlLabel control={<Checkbox size="small" checked={form.customer_accept_newsletter} onChange={(e) => handleClientFieldChange('customer_accept_newsletter', e.target.checked)} />} label={<Typography variant="body2">Accepte de recevoir les actualités</Typography>} />
                  <FormControlLabel control={<Checkbox size="small" checked={form.customer_is_active} onChange={(e) => handleClientFieldChange('customer_is_active', e.target.checked)} />} label={<Typography variant="body2">Activer</Typography>} />
                </Box>
              </Box>
            </Box>
          </Paper>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />} onClick={handleNext} sx={{ borderRadius: 3, px: 6, py: 1.5, textTransform: 'none', fontWeight: 700, fontSize: '1rem', bgcolor: '#27ae60', '&:hover': { bgcolor: '#219a52' }, boxShadow: '0 4px 14px rgba(39,174,96,0.3)' }}>Suivant — Tarification</Button>
          </Box>
        </Box>
      </Fade>

      {/* TARIFICATION */}
      <Fade in={currentStepId === 'tarif'} timeout={300} mountOnEnter unmountOnExit>
        <Box sx={{ px: 2, py: 3 }}>
          <Paper elevation={0} sx={{ p: 0, borderRadius: 3, border: '1px solid #e0e4ea', overflow: 'hidden' }}>
            <Box sx={{ bgcolor: '#2c3e50', color: 'white', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1 }}><ReceiptIcon /><Typography variant="h6" fontWeight={700}>Tarification</Typography></Box>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <FormControl fullWidth size="small"><InputLabel>Tarif de base (plan)</InputLabel><Select value={form.pricing_plan_id || ''} label="Tarif de base (plan)" onChange={(e) => setForm((f) => ({ ...f, pricing_plan_id: e.target.value as number, pricing_pack_id: '', pack_name: '' }))} sx={{ borderRadius: 2 }}><MenuItem value="">—</MenuItem>{plans.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}</Select></FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField fullWidth size="small" label="Catégorie" disabled value={selectedCategoryName} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField fullWidth size="small" label="Offre" value={form.pack_name} placeholder="Choisir une offre" onClick={openOfferModal} InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><SearchIcon onClick={openOfferModal} style={{ cursor: 'pointer' }} /></InputAdornment> }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, cursor: 'pointer' } }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField fullWidth size="small" label="Durée (jours)" value={computedDuration || form.duration} InputProps={{ readOnly: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f5f5f5' } }} />
                </Grid>
              </Grid>

              <Paper sx={{ mt: 3, p: 2.5, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e4ea' }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <Typography variant="caption" color="text.secondary">Tarif/jour HT</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={form.daily_rate || ''}
                      onChange={(e) => {
                        // Only "Offre libre" should allow manual modification of daily rate
                        if (form.pack_name !== 'Offre libre') return;
                        setForm((f) => ({ ...f, daily_rate: parseFloat(e.target.value) || 0 }));
                      }}
                      InputProps={{
                        readOnly: Boolean(form.pack_name) && form.pack_name !== 'Offre libre',
                        endAdornment: <InputAdornment position="end">€</InputAdornment>,
                      }}
                      sx={{
                        mt: 0.5,
                        '& .MuiOutlinedInput-root': { borderRadius: 2 },
                        bgcolor: Boolean(form.pack_name) && form.pack_name !== 'Offre libre' ? '#f5f5f5' : undefined,
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <Typography variant="caption" color="text.secondary">Remise (%)</Typography>
                    <TextField fullWidth size="small" value={form.discount_pct} onChange={(e) => setForm((f) => ({ ...f, discount_pct: e.target.value }))} sx={{ mt: 0.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <Typography variant="caption" color="text.secondary">Remise (jours offerts)</Typography>
                    <TextField fullWidth size="small" value={form.discount_days} onChange={(e) => setForm((f) => ({ ...f, discount_days: e.target.value }))} sx={{ mt: 0.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <Typography variant="caption" color="text.secondary">Location HT</Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#2c3e50', mt: 0.5 }}>{baseRentalHT.toFixed(2)} €</Typography>
                    <Typography variant="caption" color="text.secondary">{effectiveDays}j x {Number(form.daily_rate || 0).toFixed(2)}€</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <Typography variant="caption" color="text.secondary">Total HT</Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#2c3e50', mt: 0.5 }}>{totalHT.toFixed(2)} €</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <Typography variant="caption" color="text.secondary">Total TTC</Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#27ae60', mt: 0.5 }}>{totalTTC.toFixed(2)} €</Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Franchise</Typography>
                    <TextField
                      size="small"
                      value={form.franchise_amount}
                      onChange={(e) => {
                        // Franchise must come from the selected pack (not user-editable).
                        if (form.pack_name) return;
                        setForm((f) => ({ ...f, franchise_amount: e.target.value }));
                      }}
                      inputProps={{ inputMode: 'decimal' }}
                      sx={{
                        ml: 1,
                        width: 120,
                        '& .MuiOutlinedInput-root': { borderRadius: 2 },
                        bgcolor: form.pack_name ? '#f5f5f5' : undefined,
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">€</InputAdornment>,
                        readOnly: Boolean(form.pack_name),
                      }}
                    />
                  </Box>
                  {resteAPayer > 0 && <Box><Typography variant="caption" color="error.main" fontWeight={700}>Reste à payer</Typography><Typography variant="h6" fontWeight={700} color="error.main">{resteAPayer.toFixed(2)} €</Typography></Box>}
                </Box>
              </Paper>

              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, color: '#2c3e50' }}>Prestations</Typography>
              <TableContainer sx={{ borderRadius: 2, border: '1px solid #e0e4ea' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#1e88e5' }}>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>Action</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>Code</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>Libellé</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>Type tarif</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>Qté</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>Tarif HT</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>TVA %</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>Tarif TTC</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>Sous total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {form.prestations.length === 0 ? (
                      <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3, color: 'text.secondary' }}>Aucune prestation</TableCell></TableRow>
                    ) : form.prestations.map((row: any, index: number) => {
                      const tva = row.tax_rate ?? 20;
                      const tarifHT = row.unit_rate ?? 0;
                      const tarifTTC = tarifHT * (1 + tva / 100);
                      const sousTotal = (row.quantity ?? 1) * tarifTTC;
                      return (
                        <TableRow key={index} sx={{ bgcolor: row.is_included ? '#f0fff4' : undefined }}>
                          <TableCell>
                          {row.is_included ? (
                            <Chip label="Incluse" size="small" color="success" sx={{ fontSize: '0.7rem' }} />
                          ) : (
                            <IconButton size="small" color="error" onClick={() => removePrestationRow(index)}>
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          )}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{row.prestation_id}</TableCell>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.pricing_mode === 'daily' ? 'Jour' : 'Forfait'}</TableCell>
                          <TableCell>
                          {row.is_included && !isOffreLibre ? (
                            row.quantity
                          ) : (
                              <TextField
                                type="number"
                                size="small"
                                value={row.quantity ?? 1}
                                onChange={(e) => updatePrestationRow(index, 'quantity', parseInt(e.target.value, 10) || 1)}
                                inputProps={{ min: 1 }}
                                sx={{ width: 60 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                              {Number(tarifHT).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell>{tva}%</TableCell>
                          <TableCell>{Number(tarifTTC).toFixed(2)}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{Number(sousTotal).toFixed(2)}</TableCell>
                        </TableRow>
                      );
                    })}
                    {form.prestations.length > 0 && (
                      <TableRow sx={{ bgcolor: '#f5f6fa' }}>
                        <TableCell colSpan={5} /><TableCell sx={{ fontWeight: 700 }}>{prestationsTotalHT.toFixed(2)}</TableCell><TableCell /><TableCell sx={{ fontWeight: 700 }}>{prestationsTotalTTC.toFixed(2)}</TableCell><TableCell sx={{ fontWeight: 700, color: '#2c3e50', fontSize: '0.9rem' }}>{prestationsTotalTTC.toFixed(2)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Ajouter une prestation</InputLabel>
                  <Select
                    value=""
                    label="Ajouter une prestation"
                    onChange={(e) => {
                      const p = prestations.find((x) => x.id === Number(e.target.value));
                      if (p) addPrestationRow(p);
                    }}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">—</MenuItem>
                    {prestations
                      .filter((p) => !form.prestations.some((r: any) => r.prestation_id === p.id))
                      .map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Paper>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              disabled={loadingBaseRate}
              sx={{ borderRadius: 3, px: 6, py: 1.5, textTransform: 'none', fontWeight: 700, fontSize: '1rem', bgcolor: '#27ae60', '&:hover': { bgcolor: '#219a52' }, boxShadow: '0 4px 14px rgba(39,174,96,0.3)' }}
            >
              {loadingBaseRate ? 'Chargement...' : 'Suivant — Règlement'}
            </Button>
          </Box>
        </Box>
      </Fade>

      {/* REGLEMENT */}
      <Fade in={currentStepId === 'reglement'} timeout={300} mountOnEnter unmountOnExit>
        <Box sx={{ px: 2, py: 3 }}>
          <Paper elevation={0} sx={{ p: 0, borderRadius: 3, border: '1px solid #e0e4ea', overflow: 'hidden' }}>
            <Box sx={{ bgcolor: '#2c3e50', color: 'white', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1 }}><PaymentIcon /><Typography variant="h6" fontWeight={700}>Règlement</Typography></Box>
            <Box sx={{ p: 3 }}>
              <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e4ea' }}>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <Box><Typography variant="caption" color="text.secondary">Total TTC</Typography><Typography variant="h6" fontWeight={700} sx={{ color: '#2c3e50' }}>{totalTTC.toFixed(2)} €</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary">Total payé</Typography><Typography variant="h6" fontWeight={700} sx={{ color: '#27ae60' }}>{totalPayments.toFixed(2)} €</Typography></Box>
                  <Box><Typography variant="caption" color={resteAPayer > 0 ? 'error.main' : 'text.secondary'} fontWeight={700}>Reste à payer</Typography><Typography variant="h6" fontWeight={700} color={resteAPayer > 0 ? 'error.main' : '#27ae60'}>{resteAPayer.toFixed(2)} €</Typography></Box>
                </Box>
              </Paper>

              <TableContainer sx={{ borderRadius: 2, border: '1px solid #e0e4ea' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#1e88e5' }}>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>Mode</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>Libellé</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>Montant</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>Échéance</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>Agence</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>Agent</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {form.payments.length === 0 ? (
                      <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3, color: 'text.secondary' }}>Aucun paiement ajouté</TableCell></TableRow>
                    ) : form.payments.map((row: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 100 }}><Select value={row.type || 'reglement'} onChange={(e) => updatePaymentRow(index, 'type', e.target.value)} sx={{ borderRadius: 2 }}><MenuItem value="reglement">Règlement</MenuItem><MenuItem value="garantie">Garantie</MenuItem></Select></FormControl>
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 120 }}><Select value={row.mode || ''} onChange={(e) => updatePaymentRow(index, 'mode', e.target.value)} sx={{ borderRadius: 2 }} displayEmpty><MenuItem value="">—</MenuItem>{PAYMENT_MODES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}</Select></FormControl>
                        </TableCell>
                        <TableCell><TextField size="small" value={row.label || ''} onChange={(e) => updatePaymentRow(index, 'label', e.target.value)} sx={{ width: 120, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></TableCell>
                        <TableCell><TextField size="small" type="number" value={row.amount || ''} onChange={(e) => updatePaymentRow(index, 'amount', e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></TableCell>
                        <TableCell><TextField size="small" type="date" value={row.payment_date || ''} onChange={(e) => updatePaymentRow(index, 'payment_date', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></TableCell>
                        <TableCell><TextField size="small" type="date" value={row.due_date || ''} onChange={(e) => updatePaymentRow(index, 'due_date', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 100 }}><Select value={row.agency_id || ''} onChange={(e) => updatePaymentRow(index, 'agency_id', e.target.value)} sx={{ borderRadius: 2 }} displayEmpty><MenuItem value="">—</MenuItem>{agencies.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}</Select></FormControl>
                        </TableCell>
                        <TableCell><TextField size="small" value={row.agent_name || ''} InputProps={{ readOnly: true }} sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f5f5f5' } }} /></TableCell>
                        <TableCell><IconButton size="small" color="error" onClick={() => removePaymentRow(index)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button startIcon={<AddIcon />} onClick={addPaymentRow} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>Ajouter un paiement</Button>
                {isEdit && (
                  <Button variant="contained" startIcon={<GavelIcon />} onClick={() => navigate(`/reservations/${id}/contract`)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: '#8e44ad', '&:hover': { bgcolor: '#7d3c98' }, ml: 'auto' }}>Procéder au contrat</Button>
                )}
              </Box>
            </Box>
          </Paper>
        </Box>
      </Fade>

      {/* COMMENTS */}
      <Fade in={currentStepId === 'comments'} timeout={300} mountOnEnter unmountOnExit>
        <Box sx={{ px: 2, py: 3 }}>
          <Paper elevation={0} sx={{ p: 0, borderRadius: 3, border: '1px solid #e0e4ea', overflow: 'hidden' }}>
            <Box sx={{ bgcolor: '#2c3e50', color: 'white', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CommentIcon />
              <Typography variant="h6" fontWeight={700}>Commentaires</Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              {!isEdit ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Note interne facultative — elle sera enregistrée après la création de la réservation (bouton Enregistrer).
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Votre commentaire..."
                    value={pendingCreateComment}
                    onChange={(e) => setPendingCreateComment(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      placeholder="Ajouter un commentaire..."
                      value={newCommentText}
                      onChange={(e) => setNewCommentText(e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddComment}
                      disabled={!newCommentText.trim()}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: '#2c3e50', '&:hover': { bgcolor: '#34495e' }, alignSelf: 'flex-start', px: 3 }}
                    >
                      Envoyer
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  {loadingComments ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
                  ) : comments.length === 0 ? (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>Aucun commentaire</Typography>
                  ) : comments.map((c: any) => (
                    <Paper key={c.id} elevation={0} sx={{ p: 2, mb: 1.5, borderRadius: 2, border: '1px solid #e8ecf1', bgcolor: '#fafbfc' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#2c3e50' }}>
                            {c.user?.name || c.user?.first_name || 'Agent'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.created_at ? new Date(c.created_at).toLocaleString('fr-FR') : ''}
                          </Typography>
                        </Box>
                        <IconButton size="small" color="error" onClick={() => handleDeleteComment(c.id)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1 }}>{c.content}</Typography>
                    </Paper>
                  ))}
                </>
              )}
            </Box>
          </Paper>
        </Box>
      </Fade>

      {/* DOCUMENTS (edit) */}
      <Fade in={currentStepId === 'documents'} timeout={300} mountOnEnter unmountOnExit>
        <Box sx={{ px: 2, py: 3 }}>
          <Paper elevation={0} sx={{ p: 0, borderRadius: 3, border: '1px solid #e0e4ea', overflow: 'hidden' }}>
            <Box sx={{ bgcolor: '#2c3e50', color: 'white', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <FolderOutlinedIcon />
              <Typography variant="h6" fontWeight={700}>Documents et pièces jointes</Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Raccourcis vers les pièces usuelles (RIB, KBIS, CNI, permis…) : déposez-les sur la fiche client. États des lieux liés au sous-client.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                <Button variant="contained" startIcon={<DescriptionIcon />} onClick={handleDownloadPdf} disabled={!id} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
                  Télécharger le PDF (bon / devis)
                </Button>
                {(form.sub_client_id || selectedExistingClient?.id) != null && Number(form.sub_client_id || selectedExistingClient?.id) > 0 && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => window.open(`${window.location.origin}/sub-clients/${form.sub_client_id || selectedExistingClient?.id}#zone-documents`, '_blank', 'noopener,noreferrer')}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                      Fiche client — pièces jointes
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => window.open(`${window.location.origin}/condition-assessments?subClientId=${form.sub_client_id || selectedExistingClient?.id}`, '_blank', 'noopener,noreferrer')}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                      États des lieux
                    </Button>
                  </>
                )}
                {isEdit && (
                  <Button variant="outlined" startIcon={<GavelIcon />} onClick={() => navigate(`/reservations/${id}/contract`)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                    Contrat (signature)
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        </Box>
      </Fade>

      {/* MAILING (edit) */}
      <Fade in={currentStepId === 'mailings'} timeout={300} mountOnEnter unmountOnExit>
        <Box sx={{ px: 2, py: 3 }}>
          <Paper elevation={0} sx={{ p: 0, borderRadius: 3, border: '1px solid #e0e4ea', overflow: 'hidden' }}>
            <Box sx={{ bgcolor: '#2c3e50', color: 'white', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailOutlinedIcon />
              <Typography variant="h6" fontWeight={700}>Mailing</Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Envoi des documents par e-mail et historique des envois.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<DescriptionIcon />}
                  onClick={handleSendBonDeCommande}
                  disabled={sendingBonLoading || loadingMailings}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: '#2c3e50', '&:hover': { bgcolor: '#34495e' }, px: 3 }}
                >
                  {sendingBonLoading ? 'Envoi...' : 'Bon de commande'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<DescriptionIcon />}
                  onClick={handleSendContract}
                  disabled={sendingContractLoading || loadingMailings}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' }, px: 3 }}
                >
                  {sendingContractLoading ? 'Envoi...' : 'Contrat de location'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<ForwardToInboxIcon />}
                  onClick={openRelanceDialog}
                  disabled={loadingMailings}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                >
                  Message personnalisé (relance)
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (!id) return;
                    setLoadingMailings(true);
                    reservationService.getMailings(Number(id))
                      .then((res) => setMailings(res.data.data || []))
                      .catch(() => setMailings([]))
                      .finally(() => setLoadingMailings(false));
                  }}
                  disabled={loadingMailings}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                >
                  {loadingMailings ? 'Chargement...' : 'Rafraîchir'}
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {loadingMailings ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : mailings.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  Aucun envoi enregistré
                </Typography>
              ) : (
                <TableContainer sx={{ borderRadius: 2, border: '1px solid #e0e4ea' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#1e88e5' }}>
                        <TableCell sx={{ fontWeight: 700, color: 'white' }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'white' }}>Objet</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'white' }}>Envoyé à</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'white' }}>Envoyé le</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'white' }}>Statut</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'white' }}>Envoyé par</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mailings.map((m: any) => (
                        <TableRow key={m.id}>
                          <TableCell>{RESERVATION_MAILING_TYPE_LABELS[m.type] || m.type || '—'}</TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>{m.subject || '—'}</TableCell>
                          <TableCell>{m.sent_to || '—'}</TableCell>
                          <TableCell>
                            {m.sent_at ? new Date(m.sent_at).toLocaleString('fr-FR') : '—'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={m.status || 'sent'}
                              size="small"
                              color={m.status === 'sent' ? 'success' : 'default'}
                              sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          <TableCell>{m.sender?.name || m.sender?.first_name || m.sent_by || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Paper>
        </Box>
      </Fade>

      {/* RELANCE E-MAIL (edit) */}
      <Dialog open={relanceDialogOpen} onClose={() => !sendingRelance && setRelanceDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: '#2c3e50', color: 'white', fontWeight: 700 }}>Message personnalisé (relance)</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Texte libre ; l’e-mail est mis en forme avec les visuels et coordonnées des modèles (départ / retour).
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Objet"
            value={relanceSubject}
            onChange={(e) => setRelanceSubject(e.target.value)}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth
            size="small"
            label="Destinataire"
            type="email"
            value={relanceSentTo}
            onChange={(e) => setRelanceSentTo(e.target.value)}
            helperText="Par défaut : e-mail client de la réservation. Laissez vide pour réutiliser celui du dossier."
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth
            multiline
            minRows={8}
            label="Message"
            value={relanceBody}
            onChange={(e) => setRelanceBody(e.target.value)}
            placeholder="Bonjour,&#10;&#10;Nous revenons vers vous concernant…"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e4ea' }}>
          <Button onClick={() => setRelanceDialogOpen(false)} disabled={sendingRelance} sx={{ textTransform: 'none' }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSendRelance}
            disabled={sendingRelance || !relanceSubject.trim() || !relanceBody.trim()}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#2c3e50', '&:hover': { bgcolor: '#34495e' } }}
          >
            {sendingRelance ? 'Envoi…' : 'Envoyer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CLIENT SEARCH DIALOG */}
      <Dialog open={clientSearchDialogOpen} onClose={() => setClientSearchDialogOpen(false)} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ bgcolor: '#2c3e50', color: 'white', px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700}>Rechercher un client</Typography>
          <IconButton onClick={() => setClientSearchDialogOpen(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField fullWidth size="small" placeholder="nom, prénom, N° Client, téléphone, email" value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearchClients()} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment> }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <Button variant="contained" onClick={handleSearchClients} disabled={loadingClientSearch} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, bgcolor: '#2c3e50', '&:hover': { bgcolor: '#34495e' } }}>{loadingClientSearch ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Rechercher'}</Button>
          </Box>
          {!loadingClientSearch && clientSearchResults.length === 0 && <Box sx={{ textAlign: 'center', py: 4 }}><PersonIcon sx={{ fontSize: 48, color: '#cfd8dc', mb: 1 }} /><Typography color="text.secondary">Aucun client trouvé.</Typography></Box>}
          {clientSearchResults.length > 0 && (
            <TableContainer sx={{ borderRadius: 2, border: '1px solid #e0e4ea' }}>
              <Table size="small">
                <TableHead><TableRow sx={{ bgcolor: '#1e88e5' }}>{['Numéro', 'Nom', 'Prénom', 'Code postal', 'Ville', 'Téléphone', 'Email', 'Entreprise', 'Actif', 'Actions'].map((h) => <TableCell key={h} sx={{ fontWeight: 700, color: 'white', whiteSpace: 'nowrap' }}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>
                  {clientSearchResults.map((row, idx) => (
                    <TableRow key={row.id} hover sx={{ bgcolor: idx % 2 === 0 ? 'white' : '#f0f7ff', cursor: 'pointer' }}>
                      <TableCell sx={{ fontWeight: 600 }}>{row.id}</TableCell>
                      <TableCell>{row.nom || row.name}</TableCell>
                      <TableCell>{row.prenom}</TableCell>
                      <TableCell>{row.postal_code || ''}</TableCell>
                      <TableCell>{row.city || ''}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.phone || ''}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.email}</TableCell>
                      <TableCell>{row.company_name ? 'OUI' : 'NON'}</TableCell>
                      <TableCell><Chip label={row.is_active ? 'OUI' : 'NON'} size="small" color={row.is_active ? 'success' : 'default'} sx={{ fontWeight: 600, fontSize: '0.7rem' }} /></TableCell>
                      <TableCell><Button size="small" variant="contained" onClick={() => selectSubClient(row)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#2c3e50', '&:hover': { bgcolor: '#34495e' } }}>Sélectionner</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      {/* CLIENT CONFIRM DIALOG */}
      <Dialog open={clientConfirmDialogOpen} onClose={() => setClientConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#2c3e50', color: 'white', fontWeight: 700 }}>
          {selectedExistingClient && clientModified ? 'Confirmer la modification du client' : 'Confirmer la création du client'}
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {selectedExistingClient && clientModified
              ? `Les informations du client N° ${selectedExistingClient.id} seront mises à jour lors de l'enregistrement de la réservation.`
              : "Un nouveau client sera créé lors de l'enregistrement de la réservation."}
          </Typography>
          <Box sx={{ bgcolor: '#f5f6fa', p: 2, borderRadius: 2 }}>
            <Typography variant="body2"><strong>{form.customer_civility} {form.customer_first_name} {form.customer_last_name}</strong></Typography>
            {form.customer_email && <Typography variant="caption" color="text.secondary">{form.customer_email}</Typography>}
            {form.customer_phone && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{form.customer_phone}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setClientConfirmDialogOpen(false)} sx={{ textTransform: 'none' }}>Annuler</Button>
          <Button variant="contained" onClick={() => { setClientConfirmDialogOpen(false); setStep(tarifStepIndex >= 0 ? tarifStepIndex : step + 1); }} sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#27ae60', '&:hover': { bgcolor: '#219a52' } }}>Confirmer et continuer</Button>
        </DialogActions>
      </Dialog>

      {/* VEHICLE SELECTION MODAL */}
      <Dialog open={vehicleModalOpen} onClose={() => { setVehicleModalOpen(false); setCategoryForModal(null); }} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ bgcolor: '#2c3e50', color: 'white', px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700}>{categoryForModal ? `${categoryForModal.category_name} — Choisissez un véhicule` : 'Choisissez un véhicule'}</Typography>
          <IconButton onClick={() => { setVehicleModalOpen(false); setCategoryForModal(null); }} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <TextField fullWidth size="medium" label="Rechercher un véhicule" placeholder="Marque, modèle ou immatriculation…" value={vehicleSearchText} onChange={(e) => setVehicleSearchText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary', fontSize: 26 }} /></InputAdornment> }} sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '1rem' }, '& .MuiInputBase-input': { py: 1.35 } }} />
          <TableContainer sx={{ borderRadius: 2, border: '1px solid #e0e4ea' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#2c3e50' }}>
                  <TableCell sx={{ fontWeight: 700, color: 'white' }}>Image</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'white' }}>Marque / Modèle</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'white' }}>Immat.</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'white' }}>Places</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'white' }}>Portes</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'white' }}>Transmission</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'white' }}>Carburant</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'white' }}>Km</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'white' }}>Statut</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'white' }} align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredModalVehicles.length === 0 ? (
                  <TableRow><TableCell colSpan={10} align="center" sx={{ py: 3 }}>Aucun véhicule trouvé</TableCell></TableRow>
                ) : filteredModalVehicles.map((v: AvailabilityVehicleItem, idx: number) => {
                  const isStopSell = Boolean(v.is_stop_sell_blocked);
                  return (
                    <TableRow key={v.id} hover sx={{ bgcolor: isStopSell ? '#fff8f7' : (idx % 2 === 0 ? 'white' : '#fafbfd'), cursor: 'pointer', '&:hover': { bgcolor: isStopSell ? '#ffeceb' : '#f0f4f8' }, opacity: isStopSell ? 0.75 : 1 }} onClick={() => requestVehicleSelection(v)}>
                      <TableCell>
                        <Avatar src={v.primary_image_url ?? undefined} variant="rounded" sx={{ width: 48, height: 36, bgcolor: '#eceff1' }}><DirectionsCarIcon fontSize="small" sx={{ color: '#90a4ae' }} /></Avatar>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{v.marque} {v.modele}</TableCell>
                      <TableCell><Chip label={v.registration_number} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem' }} /></TableCell>
                      <TableCell>{v.nb_places || '–'}</TableCell>
                      <TableCell>{v.nb_portes || '–'}</TableCell>
                      <TableCell>{v.transmission_type || '–'}</TableCell>
                      <TableCell>{v.fuel_type || '–'}</TableCell>
                      <TableCell>{v.mileage ? `${v.mileage.toLocaleString('fr-FR')} km` : '–'}</TableCell>
                      <TableCell>
                        {isStopSell ? (
                          <Tooltip title="Stop sell actif sur cette période — une confirmation sera demandée">
                            <Chip label="Stop sell" size="small" color="error" sx={{ fontSize: '0.65rem' }} />
                          </Tooltip>
                        ) : v.maintenance_status && v.maintenance_status !== 'ready' ? (
                          <Tooltip title={v.maintenance_status === 'maintenance_needed' ? 'Maintenance requise' : 'Maintenance à prévoir'}>
                            <Chip icon={<WarningAmberIcon />} label={v.maintenance_status === 'maintenance_needed' ? 'Maintenance' : 'À prévoir'} size="small" color="warning" sx={{ fontSize: '0.65rem' }} />
                          </Tooltip>
                        ) : <Chip label="OK" size="small" color="success" sx={{ fontSize: '0.65rem' }} />}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {isStopSell ? (
                          <Tooltip title="Une confirmation sera demandée pour forcer la sélection">
                            <span>
                              <Button size="small" variant="contained" onClick={() => requestVehicleSelection(v)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#2c3e50', '&:hover': { bgcolor: '#34495e' } }}>
                                Sélectionner
                              </Button>
                            </span>
                          </Tooltip>
                        ) : (
                          <Button size="small" variant="contained" onClick={() => requestVehicleSelection(v)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#2c3e50', '&:hover': { bgcolor: '#34495e' } }}>
                            Sélectionner
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>

      {/* Confirmation avant sélection forcée (stop sell) */}
      <Dialog
        open={stopSellForceOpen}
        onClose={() => { setStopSellForceOpen(false); setPendingStopSellVehicle(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box sx={{ bgcolor: '#c62828', color: 'white', px: 3, py: 2 }}>
          <Typography variant="h6" fontWeight={700}>Stop sell actif</Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            Ce véhicule est concerné par un <strong>stop sell</strong> sur la période choisie (durée ou plage non autorisée par la règle en cours).
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {pendingStopSellVehicle && (
              <>
                <strong>{pendingStopSellVehicle.marque} {pendingStopSellVehicle.modele}</strong>
                {' — '}{pendingStopSellVehicle.registration_number}
              </>
            )}
          </Typography>
          <Typography variant="body1">
            Souhaitez-vous <strong>forcer</strong> la sélection de ce véhicule malgré tout ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => { setStopSellForceOpen(false); setPendingStopSellVehicle(null); }} sx={{ textTransform: 'none', borderRadius: 2 }}>
            Annuler
          </Button>
          <Button variant="contained" onClick={confirmStopSellSelection} sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' } }}>
            Forcer la sélection
          </Button>
        </DialogActions>
      </Dialog>

      {/* ID SCANNER MODAL — creates a new sub-client and auto-selects them */}
      <IDScannerModal
        open={idScannerOpen}
        onClose={() => setIdScannerOpen(false)}
        onClientCreated={async (newId) => {
          try {
            const res = await subClientService.getById(newId);
            const sc: SubClient = res.data.data;
            selectSubClient(sc);
          } catch {
            // ignore — client was created, user can search manually
          }
          setIdScannerOpen(false);
        }}
      />

      {/* OFFER MODAL */}
      <Dialog open={offerModalOpen} onClose={() => setOfferModalOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ bgcolor: '#2c3e50', color: 'white', px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700}>Choisissez une offre commerciale</Typography>
          <IconButton onClick={() => setOfferModalOpen(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          {!form.pricing_plan_id ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><Typography color="text.secondary">Sélectionnez un plan tarifaire pour charger les offres.</Typography></Box>
          ) : !selectedVehicle?.vehicle_category_id ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {"Sélectionnez un véhicule à l'étape « Dates & Véhicule » pour afficher les offres de cette catégorie."}
            </Alert>
          ) : (
            <Grid container spacing={2.5}>
              {/* Offre libre card */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Card variant="outlined" sx={{ borderRadius: 2.5, cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center', border: '2px dashed #78909c', '&:hover': { borderColor: '#2c3e50', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', transform: 'translateY(-2px)' } }} onClick={selectOffreLibre}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="subtitle1" fontWeight={700}>Offre libre</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>Tarification libre avec chargement des tarifs par défaut</Typography>
                    <Button size="small" variant="contained" fullWidth sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#78909c', '&:hover': { bgcolor: '#607d8b' } }}>Choisir</Button>
                  </CardContent>
                </Card>
              </Grid>
              {packs.map((pack) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={pack.id}>
                  <Card variant="outlined" sx={{ borderRadius: 2.5, cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center', '&:hover': { borderColor: '#1976d2', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', transform: 'translateY(-2px)' } }} onClick={() => selectPack(pack)}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center' }}><Avatar sx={{ width: 52, height: 52, bgcolor: pack.color ? `${pack.color}20` : '#e3f2fd' }}><VerifiedIcon sx={{ color: pack.color || '#1976d2', fontSize: 28 }} /></Avatar></Box>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2c3e50' }}>{pack.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Franchise: {pack.franchise_amount != null ? `${pack.franchise_amount} €` : '—'}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Tarif/jour: {pack.default_rate != null ? `${pack.default_rate} €` : '—'}</Typography>
                      {pack.included_prestations && pack.included_prestations.length > 0 && (
                        <Box sx={{ textAlign: 'left', mb: 1.5 }}>
                          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Inclus</Typography>
                          {pack.included_prestations.slice(0, 4).map((p, i) => <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}><CheckCircleIcon sx={{ fontSize: 14, color: '#4caf50' }} /><Typography variant="caption" noWrap>{p.name}</Typography></Box>)}
                          {pack.included_prestations.length > 4 && <Typography variant="caption" color="text.secondary">+{pack.included_prestations.length - 4} autres</Typography>}
                        </Box>
                      )}
                      <Button size="small" variant="contained" fullWidth sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: pack.color || '#1976d2', '&:hover': { opacity: 0.9 } }}>Choisir cette offre</Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              {packs.length === 0 && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    Aucune offre pour ce plan et cette catégorie de véhicule. Créez un pack lié à cette catégorie dans la
                    grille tarifaire ou « Gestion des packs » (les offres sans catégorie ne s&apos;affichent plus ici pour
                    éviter des tarifs partagés entre catégories).
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReservationCreate;
